import { apiCall } from '@/utils/api';
import { getCollectionCoverage, getSingleCollection } from '@/api/collections';
import { Collection, CollectionAssets } from '@/types/collection';
import { CoverageReport } from '@/types/map';
import { IMarker, Review, ReviewOutcome } from '@/types/qa';

export const RejectedOptions = [
  {
    label: 'Rejected Quality',
    value: ReviewOutcome.RejectedQuality,
    color: 'red',
  },
  {
    label: 'Rejected Accuracy',
    value: ReviewOutcome.RejectedAccuracy,
    color: 'red',
  },
  {
    label: 'Rejected Compliance',
    value: ReviewOutcome.RejectedCompliance,
    color: 'red',
  },
  {
    label: 'Rejected Duplicate',
    value: ReviewOutcome.RejectedDuplicate,
    color: 'red',
  },
  {
    label: 'Rejected - Other',
    value: ReviewOutcome.RejectedOther,
    color: 'red',
  },
];

export const ApprovedOptions = [
  {
    label: 'Approved',
    value: ReviewOutcome.Approved,
    color: 'yellowgreen',
  },
  {
    label: 'Approved High Quality',
    value: ReviewOutcome.ApprovedHighQuality,
    color: 'green',
  },
];

export const getOutcomeViewData = (outcome: ReviewOutcome) => {
  let options = [...RejectedOptions];
  if (
    [ReviewOutcome.Approved, ReviewOutcome.ApprovedHighQuality].includes(
      outcome,
    )
  ) {
    options = [...ApprovedOptions];
  }
  return options.find(option => option.value === outcome);
};

export const getQACollectionForRegion = async (
  regionId: string,
  collectionId: string | string[] | undefined,
): Promise<{
  assets: CollectionAssets;
  collection: Collection;
  coverageReport: CoverageReport;
} | null> => {
  const REVIEW_ROUTE = `/qa/review/${regionId}/dashcam`;

  try {
    let response;
    if (!collectionId) {
      // If collectionId is not presented - request it by Region. API will select the collection with a proper sampling for you
      response = await apiCall(
        `${process.env.NEXT_PUBLIC_API}${REVIEW_ROUTE}`,
        {
          method: 'GET',
        },
      );
    } else {
      // Otherwise, get collection by ID and format the response
      const collection = await getSingleCollection(
        Array.isArray(collectionId) ? collectionId[0] : collectionId,
      );
      const coverageReport = await getCollectionCoverage(collectionId);

      // And format response the way it's expected: { collection, assets, coverageReport }
      if (collection?.keyframes && coverageReport?._doc) {
        response = {
          assets: {
            keyframes: collection.keyframes,
          },
          collection: { ...collection, _id: collection.id },
          coverageReport: coverageReport._doc,
        };
      }
    }

    // randomise the keyframes order to show different keyframes to user
    if (response && response.assets) {
      if (response.assets.keyframes) {
        response.assets.keyframes = response.assets.keyframes.sort(
          () => 0.5 - Math.random(),
        );
      }
      return response;
    } else {
      return null;
    }
  } catch (error: any) {
    return null;
  }
};

export const fetchReviews = async (): Promise<Review[] | null> => {
  const FETCH_REVIEWS_ROUTE = '/dashboard/reviews/';

  try {
    const response = await apiCall(
      `${process.env.NEXT_PUBLIC_API}${FETCH_REVIEWS_ROUTE}`,
      {
        method: 'GET',
      },
    );
    if (response?.reviews?.length) {
      return response.reviews;
    } else {
      return null;
    }
  } catch (error: any) {
    return null;
  }
};

export const createReview = async (
  collectionId: string,
  outcome: ReviewOutcome,
  notes: string,
  imageNotesObj: any,
): Promise<string | null> => {
  const errorMessage = `Error submitting Review with ID: ${collectionId}.`;
  const suggestion =
    'Please, notify Eng Team about it, for now you can hit "Skip & Get Next" while we investigate.';

  try {
    const CREATE_REVIEW_ROUTE = `/qa/review/${collectionId}`;
    const response = await apiCall(
      `${process.env.NEXT_PUBLIC_API}${CREATE_REVIEW_ROUTE}`,
      {
        method: 'POST',
      },
      { outcome, notes },
    );
    if (response && response.recorded) {
      return null;
    } else {
      return `${errorMessage} ${JSON.stringify(response)} ${suggestion}`;
    }
  } catch (error: any) {
    return `${errorMessage} ${error} ${suggestion}`;
  }
};

export const percentToPixels = (
  marker: Pick<IMarker, 'x' | 'y'>,
  dimensions: { width: number; height: number },
) => {
  return {
    x: (marker.x * dimensions.width) / 100,
    y: (marker.y * dimensions.height) / 100,
  };
};

export const pixelsToPercent = (
  marker: Pick<IMarker, 'x' | 'y'>,
  dimensions: { width: number; height: number },
) => {
  return {
    x: (marker.x * 100) / dimensions.width,
    y: (marker.y * 100) / dimensions.height,
  };
};

export const transformToRealSize = (
  marker: Pick<IMarker, 'x' | 'y'>,
  dimensions: { width: number; height: number },
) => {
  return {
    x: (marker.x * dimensions.width) / 100,
    y: (marker.y * dimensions.height) / 100,
  };
};

export const getScaleFactor = (
  width: number,
  height: number,
  maxWidth: number,
  maxHeight: number,
) => {
  let scaleFactor = maxWidth / width;
  let finalHeight = height * scaleFactor;
  while (finalHeight > maxHeight) {
    scaleFactor = scaleFactor - 0.1;
    finalHeight = height * scaleFactor;
  }
  return scaleFactor;
};

export const isToolTypeFromName = (toolName: string, type: string) => {
  const words = toolName.split(' ');
  return words.some(word => word === type);
};
